/**
 * Centralized Date Formatting Utility
 *
 * Provides consistent date/time formatting across the entire application.
 * Replaces inconsistent relative time displays (e.g., "2 hours ago") with
 * actual timestamps (e.g., "Jan 15, 2025 2:30 PM").
 *
 * Usage:
 * - formatDateTime(date) - Full date and time: "Jan 15, 2025 2:30 PM"
 * - formatDateOnly(date) - Date without time: "Jan 15, 2025"
 * - formatDateTimeLong(date) - Long format: "January 15, 2025 2:30 PM"
 */

export interface DateFormatOptions {
	showTime?: boolean;
	format?: "short" | "medium" | "long";
}

/**
 * Main date formatting function with configurable options
 *
 * @param dateString - ISO 8601 date string, Date object, or null/undefined
 * @param options - Formatting options
 * @returns Formatted date string or "N/A" for invalid/null dates
 */
export const formatDate = (
	dateString: string | Date | undefined | null,
	options?: DateFormatOptions,
): string => {
	if (!dateString) return "N/A";

	const { showTime = true, format = "medium" } = options || {};
	const date = new Date(dateString);

	// Validate date
	if (isNaN(date.getTime())) {
		console.warn(`Invalid date provided to formatDate: ${dateString}`);
		return "Invalid Date";
	}

	// Build format options
	const dateOptions: Intl.DateTimeFormatOptions = {
		year: "numeric",
		month: format === "long" ? "long" : "short",
		day: "numeric",
	};

	if (showTime) {
		dateOptions.hour = "2-digit";
		dateOptions.minute = "2-digit";
	}

	return new Intl.DateTimeFormat("en-US", dateOptions).format(date);
};

/**
 * Format date with time (default format)
 * Output: "Jan 15, 2025 2:30 PM"
 */
export const formatDateTime = (dateString: string | Date | undefined | null): string => {
	return formatDate(dateString, { showTime: true, format: "medium" });
};

/**
 * Format date without time
 * Output: "Jan 15, 2025"
 */
export const formatDateOnly = (dateString: string | Date | undefined | null): string => {
	return formatDate(dateString, { showTime: false, format: "medium" });
};

/**
 * Format date with time in long format
 * Output: "January 15, 2025 2:30 PM"
 */
export const formatDateTimeLong = (dateString: string | Date | undefined | null): string => {
	return formatDate(dateString, { showTime: true, format: "long" });
};

/**
 * Format date in short format (date only)
 * Output: "Jan 15, 2025"
 */
export const formatDateShort = (dateString: string | Date | undefined | null): string => {
	return formatDate(dateString, { showTime: false, format: "short" });
};
